/* eslint-disable max-lines */
import { Grid, MenuItem } from '@mui/material';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import {
  getAllTransformedCubs,
  getCub,
  updateCub,
  updateTransformedCubs,
  updateTransformedCubsLogic,
} from '../../api/cub';
import {
  ConvertedCubData,
  ConvertedCubLogicData,
  TableCubData,
  TransformedCubData,
} from '../../api/cub/types';
import {
  convertCubPropertyType,
  CubPropertyType,
  RegistrationUf,
  Standard,
  StatusCode,
} from '../../api/enumerations';
import { PreffixNumericTextField } from '../../components/CustomInput';
import Snackbar from '../../components/Snackbar';
import { TitleBox } from '../../components/UI/Box';
import { GridMain } from '../../components/UI/Grid';
import {
  menuItemDefaultStyle,
  StyledTextField,
  TitleTypography,
} from '../../components/UI/Typography';
import {
  ApartmentStandards,
  Constants,
  StoreStandards,
  WarehouseStandards,
} from '../../constants/adjustment';
import {
  IconHouseMS,
  IconSquareFootMS,
  IconStoreMS,
  IconTuneMS,
  IconWarehouseMS,
} from '../../constants/icons';
import { selectRegistrationUf } from '../../constants/selectOptions';
import { GlobalContext } from '../../context/global';
import useErrorMessage from '../../hooks/useErrorMessage';
import {
  DividerContainer,
  SectionTitle,
  SectionTitleContainer,
  StyledDivider,
  Subtitle,
  UfBox,
  UpdateBox,
  UpdateButton,
} from './styles';
import { Table } from './Table';

export function Adjustment(): JSX.Element {
  const [uf, setUf] = useState(0);
  const [r1low, setR1low] = useState(0);
  const [r1normal, setR1normal] = useState(0);
  const [r1high, setR1high] = useState(0);
  const [r8low, setR8low] = useState(0);
  const [r8normal, setR8normal] = useState(0);
  const [r8high, setR8high] = useState(0);
  const [r16normal, setR16normal] = useState(0);
  const [r16high, setR16high] = useState(0);
  const [cal8normal, setCal8normal] = useState(0);
  const [cal8high, setCal8high] = useState(0);
  const [csl8normal, setCsl8normal] = useState(0);
  const [csl8high, setCsl8high] = useState(0);
  const [csl16normal, setCsl16normal] = useState(0);
  const [csl16high, setCsl16high] = useState(0);
  const [gi, setGi] = useState(0);

  const [tableApartmentData, setTableApartmentData] = useState<
    TableCubData | undefined
  >(undefined);
  const [tableStoreData, setTableStoreData] = useState<
    TableCubData | undefined
  >(undefined);
  const [tableCommercialBuildingData, setTableCommercialBuildingData] =
    useState<TableCubData | undefined>(undefined);
  const [tableCommercialRoomData, setTableCommercialRoomData] = useState<
    TableCubData | undefined
  >(undefined);
  const [tableWarehouseData, setTableWarehouseData] = useState<
    TableCubData | undefined
  >(undefined);

  const { openSnackbar, setErrorMessage, setOpenSnackbar, setSnackbarMessage } =
    useContext(GlobalContext);

  const { getErrorMessage } = useErrorMessage();

  const createPropertyData = (
    data: TransformedCubData[],
    setter: (value: TableCubData) => void
  ): void => {
    const simpleItem = data.filter(
      (item) => item.constructive_standard === Standard.SIMPLE
    );
    const normalItem = data.filter(
      (item) => item.constructive_standard === Standard.NORMAL
    );
    const highItem = data.filter(
      (item) => item.constructive_standard === Standard.HIGH
    );

    setter({
      simple: simpleItem[0],
      normal: normalItem[0],
      high: highItem[0],
    });
  };

  const handleSearchUf = async (ufNumber: number): Promise<void> => {
    if (ufNumber === 0) {
      setSnackbarMessage('Selecione uma UF');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    setTableApartmentData(undefined);
    setTableCommercialBuildingData(undefined);
    setTableCommercialRoomData(undefined);
    setTableCommercialRoomData(undefined);
    setTableStoreData(undefined);
    setTableWarehouseData(undefined);
    setR1low(0);
    setR1normal(0);
    setR1high(0);
    setR8low(0);
    setR8normal(0);
    setR8high(0);
    setR16normal(0);
    setR16high(0);
    setCal8normal(0);
    setCal8high(0);
    setCsl8normal(0);
    setCsl8high(0);
    setCsl16normal(0);
    setCsl16high(0);
    setGi(0);

    try {
      const response = await getCub(ufNumber);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado.');
      }

      if (response.data) {
        setR1low(response.data.r1_baixo);
        setR1normal(response.data.r1_normal);
        setR1high(response.data.r1_alto);
        setR8low(response.data.r8_baixo);
        setR8normal(response.data.r8_normal);
        setR8high(response.data.r8_alto);
        setR16normal(response.data.r16_normal);
        setR16high(response.data.r16_alto);
        setCal8normal(response.data.cal8_normal);
        setCal8high(response.data.cal8_alto);
        setCsl8normal(response.data.csl8_normal);
        setCsl8high(response.data.csl8_alto);
        setCsl16normal(response.data.csl16_normal);
        setCsl16high(response.data.csl16_alto);
        setGi(response.data.gi);
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    }

    try {
      const response = await getAllTransformedCubs({ uf: ufNumber });

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado.');
      }

      if (response.data) {
        const apartmentItems = response.data.filter(
          (item) => item.property_type === CubPropertyType.APARTMENT
        );
        const streetStoreItems = response.data.filter(
          (item) => item.property_type === CubPropertyType.STORE
        );
        const commercialBuildingItems = response.data.filter(
          (item) => item.property_type === CubPropertyType.COMMERCIAL_BUILDING
        );
        const commercialRoomItems = response.data.filter(
          (item) => item.property_type === CubPropertyType.COMMERCIAL_ROOM
        );
        const warehouseItems = response.data.filter(
          (item) => item.property_type === CubPropertyType.WAREHOUSE
        );

        createPropertyData(apartmentItems, setTableApartmentData);
        createPropertyData(streetStoreItems, setTableStoreData);
        createPropertyData(
          commercialBuildingItems,
          setTableCommercialBuildingData
        );
        createPropertyData(commercialRoomItems, setTableCommercialRoomData);
        createPropertyData(warehouseItems, setTableWarehouseData);
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    }
  };

  const handleValues = useCallback(():
    | { cubLogic: ConvertedCubLogicData[]; cubValues: ConvertedCubData[] }
    | undefined => {
    const sourcesRaw = [
      tableApartmentData,
      tableStoreData,
      tableCommercialBuildingData,
      tableCommercialRoomData,
      tableWarehouseData,
    ];

    const isCompleteTable = (
      table: TableCubData | undefined
    ): table is Required<TableCubData> => {
      return ['simple', 'normal', 'high'].every((key) => {
        const value = table?.[key as keyof TableCubData];

        if (!value) return false;

        if (!value.logic || !value.logic.trim()) return false;
        if (!value.converted_cub) return false;

        return true;
      });
    };

    const allTablesAreComplete = sourcesRaw.every(isCompleteTable);

    if (!allTablesAreComplete) {
      return undefined;
    }

    const cubLogic = sourcesRaw.flatMap((table) => [
      { id: table.simple.id, logic: table.simple.logic },
      { id: table.normal.id, logic: table.normal.logic },
      { id: table.high.id, logic: table.high.logic },
    ]);

    const cubValues = sourcesRaw.flatMap((table) => [
      { id: table.simple.id, converted_cub: table.simple.converted_cub },
      { id: table.normal.id, converted_cub: table.normal.converted_cub },
      { id: table.high.id, converted_cub: table.high.converted_cub },
    ]);

    return {
      cubLogic,
      cubValues,
    };
  }, [
    tableApartmentData,
    tableStoreData,
    tableCommercialBuildingData,
    tableCommercialRoomData,
    tableWarehouseData,
  ]);

  const handleSubmit = async (): Promise<void> => {
    if (uf === RegistrationUf.SELECT) {
      setSnackbarMessage('Selecione uma UF!');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const values = handleValues();

    if (
      !r1low ||
      !r1normal ||
      !r1high ||
      !r8low ||
      !r8normal ||
      !r8high ||
      !r16normal ||
      !r16high ||
      !cal8normal ||
      !cal8high ||
      !csl8normal ||
      !csl8high ||
      !csl16normal ||
      !csl16high ||
      !gi ||
      !values
    ) {
      setSnackbarMessage('Todos os campos são obrigatórios!');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const updatedCubData = {
      r1_baixo: r1low,
      r1_normal: r1normal,
      r1_alto: r1high,
      r8_baixo: r8low,
      r8_normal: r8normal,
      r8_alto: r8high,
      r16_normal: r16normal,
      r16_alto: r16high,
      cal8_normal: cal8normal,
      cal8_alto: cal8high,
      csl8_normal: csl8normal,
      csl8_alto: csl8high,
      csl16_normal: csl16normal,
      csl16_alto: csl16high,
      gi,
    };

    const { cubValues, cubLogic } = values;

    try {
      const response = await updateTransformedCubs(cubValues);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado.');
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    }

    try {
      const response = await updateTransformedCubsLogic(cubLogic);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado.');
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    }

    try {
      const response = await updateCub(uf, updatedCubData);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado.');
      }

      setSnackbarMessage('Todos os valores foram atualizados');
      setErrorMessage(false);
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    }
  };

  const createUpdateFunction = (
    setter: Dispatch<SetStateAction<TableCubData | undefined>>
  ) => {
    return (
      key: keyof TableCubData,
      updates: Partial<TableCubData[typeof key]>
    ): void => {
      setter((prev) => {
        if (!prev) {
          return { [key]: updates } as TableCubData;
        }

        const currentData = prev[key];

        return {
          ...prev,
          [key]: currentData ? { ...currentData, ...updates } : updates,
        };
      });
    };
  };

  const updateApartmentTableData = createUpdateFunction(setTableApartmentData);
  const updateStoreTableData = createUpdateFunction(setTableStoreData);
  const updateCommercialBuildingTableData = createUpdateFunction(
    setTableCommercialBuildingData
  );
  const updateCommercialRoomDataTableData = createUpdateFunction(
    setTableCommercialRoomData
  );
  const updateWarehouseTableData = createUpdateFunction(setTableWarehouseData);

  return (
    <GridMain>
      <TitleBox>
        <TitleTypography>
          {IconTuneMS}
          {Constants.adjustment}
        </TitleTypography>
      </TitleBox>
      <UfBox>
        <StyledTextField
          id="uf"
          select
          label="UF"
          value={uf}
          onChange={(e) => {
            setUf(Number(e.target.value));
            handleSearchUf(Number(e.target.value));
          }}
        >
          {selectRegistrationUf().map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{ ...menuItemDefaultStyle }}
            >
              {option.label}
            </MenuItem>
          ))}
        </StyledTextField>
      </UfBox>
      <Grid container sx={{ marginBottom: '20px' }} spacing={4}>
        <Grid item xs={12}>
          <SectionTitleContainer>
            <SectionTitle>
              {IconHouseMS}
              {Constants.residence}
            </SectionTitle>
            <DividerContainer>
              <StyledDivider />
            </DividerContainer>
          </SectionTitleContainer>
        </Grid>
        <Grid item xs={12}>
          <Subtitle>{Constants.house}</Subtitle>
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="r1low"
            label="R1 baixo"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={r1low}
            setValue={setR1low}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="r1normal"
            label="R1 normal"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={r1normal}
            setValue={setR1normal}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="r1high"
            label="R1 alto"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={r1high}
            setValue={setR1high}
          />
        </Grid>
        <Grid item xs={12}>
          <Subtitle>{Constants.flat}</Subtitle>
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="r8low"
            label="R8 baixo"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={r8low}
            setValue={setR8low}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="r8normal"
            label="R8 normal"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={r8normal}
            setValue={setR8normal}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="r8high"
            label="R8 alto"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={r8high}
            setValue={setR8high}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="r16normal"
            label="R16 normal"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={r16normal}
            setValue={setR16normal}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="r16high"
            label="R16 alto"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={r16high}
            setValue={setR16high}
          />
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: '20px' }} spacing={4}>
        <Grid item xs={12}>
          <SectionTitleContainer>
            <SectionTitle>
              {IconStoreMS}
              {Constants.comerce}
            </SectionTitle>
            <DividerContainer>
              <StyledDivider />
            </DividerContainer>
          </SectionTitleContainer>
        </Grid>
        <Grid item xs={12}>
          <Subtitle>{Constants.freeFloor}</Subtitle>
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="cal8normal"
            label="CAL8 normal"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={cal8normal}
            setValue={setCal8normal}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="cal8high"
            label="CAL8 alto"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={cal8high}
            setValue={setCal8high}
          />
        </Grid>
        <Grid item xs={12}>
          <Subtitle>{Constants.store}</Subtitle>
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="csl8normal"
            label="CSL8 normal"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={csl8normal}
            setValue={setCsl8normal}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="csl8high"
            label="CSL8 alto"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={csl8high}
            setValue={setCsl8high}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="csl16normal"
            label="CSL16 normal"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={csl16normal}
            setValue={setCsl16normal}
          />
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="csl16high"
            label="CSL16 alto"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={csl16high}
            setValue={setCsl16high}
          />
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: '20px' }} spacing={4}>
        <Grid item xs={12}>
          <SectionTitleContainer>
            <SectionTitle>
              {IconWarehouseMS}
              {Constants.warehouse}
            </SectionTitle>
            <DividerContainer>
              <StyledDivider />
            </DividerContainer>
          </SectionTitleContainer>
        </Grid>
        <Grid item xs={12}>
          <Subtitle>{Constants.industry}</Subtitle>
        </Grid>
        <Grid item xs={2}>
          <PreffixNumericTextField
            required
            id="gi"
            label="GI"
            prefix="R$ "
            decimalSeparator=","
            decimalScale={2}
            maxLength={18}
            value={gi}
            setValue={setGi}
          />
        </Grid>
      </Grid>
      <SectionTitleContainer sx={{ margin: '60px 0' }}>
        <SectionTitle>
          {IconSquareFootMS}
          {Constants.cub}
        </SectionTitle>
        <DividerContainer>
          <StyledDivider />
        </DividerContainer>
      </SectionTitleContainer>
      {tableApartmentData && (
        <Table
          tableName={convertCubPropertyType(CubPropertyType.APARTMENT)}
          tableData={tableApartmentData}
          updateTableData={updateApartmentTableData}
          popularLogicValue={r8low}
          simpleLogicValue={r8low}
          normalLogicValue={r8normal}
          highLogicValue={r16high}
          luxeLogicValue={r16high}
          standardConstants={ApartmentStandards}
        />
      )}
      {tableStoreData && (
        <Table
          tableName={convertCubPropertyType(CubPropertyType.STORE)}
          tableData={tableStoreData}
          updateTableData={updateStoreTableData}
          simpleLogicValue={csl8normal}
          normalLogicValue={cal8high}
          highLogicValue={csl16high}
          standardConstants={StoreStandards}
        />
      )}
      {tableCommercialBuildingData && (
        <Table
          tableName={convertCubPropertyType(
            CubPropertyType.COMMERCIAL_BUILDING
          )}
          tableData={tableCommercialBuildingData}
          updateTableData={updateCommercialBuildingTableData}
          simpleLogicValue={csl8normal}
          normalLogicValue={cal8high}
          highLogicValue={csl16high}
          standardConstants={StoreStandards}
        />
      )}
      {tableCommercialRoomData && (
        <Table
          tableName={convertCubPropertyType(CubPropertyType.COMMERCIAL_ROOM)}
          tableData={tableCommercialRoomData}
          updateTableData={updateCommercialRoomDataTableData}
          simpleLogicValue={csl8normal}
          normalLogicValue={cal8high}
          highLogicValue={csl16high}
          standardConstants={StoreStandards}
        />
      )}
      {tableWarehouseData && (
        <Table
          tableName={convertCubPropertyType(CubPropertyType.WAREHOUSE)}
          tableData={tableWarehouseData}
          updateTableData={updateWarehouseTableData}
          simpleLogicValue={r8normal}
          normalLogicValue={r8normal}
          highLogicValue={r8normal}
          standardConstants={WarehouseStandards}
        />
      )}
      <UpdateBox>
        <UpdateButton onClick={handleSubmit}>{Constants.confirm}</UpdateButton>
      </UpdateBox>
      {openSnackbar && <Snackbar />}
    </GridMain>
  );
}
